import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useState, useMemo } from 'react'
import { updateVisitAsync } from '../actions/visits/visits.actions'
import { postGroomingAddOnsAsync } from '../actions/addons/addons.actions'
import { api } from '../services/api'
import moment from 'moment'
import { matchesFacility } from '../utils/facilityMatch'

const useGroomingVisit = () => {
	const dispatch = useDispatch()
	const loading = useSelector((state) => state.specific.visits.loading)
	const facility = useSelector((state) => state.specific.facilities.selected)
	const household = useSelector((state) => state.specific.households.selected)

	const groomingCreditTotal = useMemo(() => {
		const creds = household?.credits?.grooming || []
		return creds.filter((c) => !c.expired && !c.used && matchesFacility(c, facility?._id)).length
	}, [household, facility])

	const isGroomingMemberOnDate = useCallback((date) => {
		if (!date) {
			return false
		}
		const memberships = household?.memberships || []
		const selectedDate = moment(date)
		const validMemberships = memberships.filter(membership => {
			const isGroomingType = membership.type.includes('grooming_annual') || membership.type.includes('grooming_monthly')

			// Exclude if deactivated
			if (!isGroomingType || membership.deactivated) {
				return false
			}

			if (facility?._id && !matchesFacility(membership, facility._id)) {
				return false
			}

			// Exclude if expired is true
			if (membership.expired) {
				return false
			}

			// If end_date is set, check that the selected date is not after it (treat as inclusive)
			if (membership.end_date && selectedDate.isAfter(moment(membership.end_date).endOf('day')) && !membership.auto_renew) {
				return false
			}



			return true
		})

		return validMemberships.length > 0
	}, [household, facility])

	const groomingRates = useMemo(() => {
		return household?.groomingRates || null
	}, [household])

	const [fetchingAvailability, setFetchingAvailability] = useState(false)

	const getPriceAndDuration = useCallback(
		async (appt) => {
			if (!appt || !facility) {
				return null
			}
			try {
				setFetchingAvailability(true)
				const { _id: facilityId } = facility
				const { grooming_service_type, matted, trim_amount, add_on_services, petId } = appt

				const dematting = matted > '' ? matted : 'none'
				const trimAmount = trim_amount > '' ? trim_amount : 'none'

				const url = `/groomingPriceAndDuration?service=${grooming_service_type.trim()}&petId=${petId.trim()}&facilityId=${facilityId.trim()}&dematting=${dematting.trim()}&trim_amount=${trimAmount.trim()}`
				const { data } = await api.post(url, { add_on_services })
				if (data.data && data.data.length > 0) {
					return data.data[0]
				}
			} catch (e) {
				console.log(e)
				return null
			} finally {
				setFetchingAvailability(false)
			}
		},
		[facility]
	)

	const getGeneralAvailability = useCallback(
		async (appt) => {
			if (!appt || !facility) {
				return null
			}
			try {
				setFetchingAvailability(true)
				const { _id: facilityId } = facility
				const { grooming_service_type, petId } = appt

				const startDate = moment().startOf('month').format('YYYY-MM-DD')

				const url = `/groomingAvailability?service=${grooming_service_type}&petId=${petId}startDate=${startDate}&facilityId=${facilityId}&byDuration=100`
				const { data } = await api.post(url)
				if (data.data && data.data.length > 0) {
					return data.data[0]
				}
				if (data.errors && data.errors.length > 0) {
					return { errors: data.errors[0] }
				}
			} catch (e) {
				console.log(e)
				return { errors: e }
			} finally {
				setFetchingAvailability(false)
			}
		},
		[facility]
	)

	const getTimesIn24HourFormat = useCallback((appt) => {
		if (!appt) {
			return { pickup_time: null, pickup_end_time: null, dropoff_time: null }
		}

		const { dropoff_time: unformattedDropoffTime, duration } = appt

		const dropoff_time = moment(unformattedDropoffTime, 'hh:mm a').format('HH:mm')

		let pickup_time = moment(dropoff_time, 'HH:mm').add(30 + parseInt(duration, 10), 'minutes')
		const remainder = 5 - (pickup_time.minute() % 5)
		if (remainder === 4 || remainder === 3) {
			const newone = 5 - remainder
			pickup_time.subtract(newone, 'minutes')
		} else {
			pickup_time.add(remainder, 'minutes')
		}
		pickup_time = pickup_time.format('HH:mm')
		const pickup_end_time = moment(pickup_time, 'HH:mm').add(90, 'minutes').format('HH:mm')

		return { pickup_time, pickup_end_time, dropoff_time }
	}, [])

	const getAvailability = useCallback(
		async (appt) => {
			if (!appt || !facility) {
				return null
			}
			try {
				console.log('APPPPPPT::::::::', appt)

				setFetchingAvailability(true)
				const { _id: facilityId } = facility
				const {
					grooming_service_type,
					matted,
					trim_amount,
					add_on_services,
					month,
					year,
					petId,
					preferred_staff_id
				} = appt

				const dematting = matted > '' ? matted : 'none'
				const trimAmount = trim_amount > '' ? trim_amount : 'none'

				let momentDate = month > -1 ? moment().set('month', month) : moment()
				if (year > -1) {
					momentDate = momentDate.set('year', year)
				}

				const startDate =
					month > -1
						? momentDate.clone().startOf('month').format('YYYY-MM-DD')
						: momentDate.clone().format('YYYY-MM-DD')
				const endDate =
					month > -1
						? momentDate.clone().endOf('month').format('YYYY-MM-DD')
						: momentDate.clone().add(3, 'month').endOf('month').format('YYYY-MM-DD')

				let url = `/groomingAvailability?service=${grooming_service_type}&petId=${petId}&startDate=${startDate}&endDate=${endDate}&facilityId=${facilityId}&dematting=${dematting}&trim_amount=${trimAmount}`
				if (preferred_staff_id && preferred_staff_id > '' && preferred_staff_id !== 'none') {
					url += `&preferred_staff_id=${preferred_staff_id}`
				}

				if (appt._id) url += `&appt_id=${appt._id}`

				const { data } = await api.post(url, { add_on_services })
				if (data.data && data.data.length > 0) {
					return data.data[0]
				}
			} catch (e) {
				console.log(e)
				return null
			} finally {
				setFetchingAvailability(false)
			}
		},
		[facility]
	)

	const updateDateAndTime = useCallback(
		async ({ visitId, updates = {} }) => {
			const body = {
				visit_id: visitId,
				updates
			}
			await updateVisitAsync(dispatch, body)
		},
		[dispatch]
	)

	const confirmAndBook = useCallback(
		async ({ visitId, updates = {} }) => {
			const body = {
				visit_id: visitId,
				updates
			}
			const res = await updateVisitAsync(dispatch, body)
			if (res) {
				return res
			}
		},
		[dispatch]
	)

	const update = useCallback(
		async ({ visitId, updates = {} }) => {
			const body = {
				visit_id: visitId,
				updates
			}
			await updateVisitAsync(dispatch, body)
		},
		[dispatch]
	)

	const sendReferenceImageRequest = useCallback(async ({ visitId }) => {
		if (!visitId) {
			return null
		}
		try {
			const data = await api.post(`/sendReferenceImageEmailToClient/${visitId}`)
			return data
		} catch (e) {
			console.log(e)
			return null
		}
	}, [])

	const uploadReferencePhoto = useCallback(
		async ({ visitId, file }) => {
			if (!visitId || !file) {
				dispatch({ type: 'UPDATE_VISIT_FAIL' })
				return null
			}

			try {
				dispatch({ type: 'POST_VISIT' })
				const data = new FormData()
				data.append('reference_photo', file)
				const updated = await api.put(`/uploadVisitImage/${visitId}?type=reference_photo_url`, data)
				dispatch({
					type: 'UPDATE_VISIT_PHOTO',
					payload: { key: 'reference_photo_url', data: updated.data.reference_photo_url }
				})
				dispatch({ type: 'POST_VISIT_SUCCESS' })
				return updated
			} catch (e) {
				dispatch({ type: 'UPDATE_VISIT_FAIL' })
				return null
			}
		},
		[dispatch]
	)

	const updateAddOns = useCallback(
		async ({ visitId, petId, addons = [] }) => {
			if (!visitId || petId === '') {
				dispatch({ type: 'UPDATE_VISIT_FAIL' })
				return
			}

			const addOnsForPayload = addons.map((addon) => {
				const { _id: rawId, addon_id: existingAddOnId, category, type, name, addon_choice_id } = addon
				const record = {
					addon_id: existingAddOnId || rawId,
					category,
					type,
					name
				}
				if (addon_choice_id) {
					record['addon_choice_id'] = addon_choice_id
				}

				return record
			})

			const payload = {
				pet_id: petId,
				addons: addOnsForPayload
			}
			await postGroomingAddOnsAsync(dispatch, visitId, payload)
		},
		[dispatch]
	)

	return {
		facility,
		loading,
		update,
		updateAddOns,
		updateDateAndTime,
		uploadReferencePhoto,
		sendReferenceImageRequest,
		getAvailability,
		fetchingAvailability,
		getPriceAndDuration,
		confirmAndBook,
		getGeneralAvailability,
		getTimesIn24HourFormat,
		groomingCreditTotal,
		groomingRates,
		isGroomingMemberOnDate
	}
}

export default useGroomingVisit
